/**
 * Location Performance — the Analytics view.
 *
 * The table answers "what are the numbers per location"; this answers "which
 * locations carry the work, and which are struggling with it". Both read the
 * same rows, so switching tabs never re-queries.
 *
 * Everything here is labelled with the grouped dimension rather than the word
 * "Location", because the report groups by whatever the drawer chose.
 */
import { useMemo, type JSX } from 'react'
import { Chart } from 'primereact/chart'
import { AlertTriangle, Building2, Clock, Gauge, ListOrdered, ShieldCheck, Timer } from 'lucide-react'

import type { LocationPerformanceRow } from '../../../hooks/useLocationPerformanceReport.js'
import {
  AnalyticsCanvas,
  AttentionList,
  CardEmpty,
  CardGrid,
  ChartCard,
  HeroBand,
  KpiRow,
  Meter,
  RankTable,
  SectionLabel,
  StatTile,
  type AttentionItem,
} from '../analytics/AnalyticsKit.js'
import {
  VIZ,
  barDataset,
  barOptions,
  formatCount,
  formatPct,
  formatSeconds,
  ratePct,
  sumBy,
  weightedMean,
} from '../analytics/viz.js'

/** How many groups a per-group chart shows before the tail is dropped. */
const CHART_GROUPS = 8
/** Breach rate past this puts a group in the attention queue. */
const BREACH_ALERT = 10

export default function LocationPerformanceAnalytics({
  rows,
  groupLabel,
}: {
  rows: readonly LocationPerformanceRow[]
  groupLabel: string
}): JSX.Element {
  const totals = useMemo(() => {
    const conversations = sumBy(rows, (r) => r.conversations)
    const slaMeasured = sumBy(rows, (r) => r.slaMeasured)
    const slaBreached = sumBy(rows, (r) => r.slaBreached)
    const olaMeasured = sumBy(rows, (r) => r.olaMeasured)
    const olaBreached = sumBy(rows, (r) => r.olaBreached)
    const csatCount = sumBy(rows, (r) => r.csatCount)
    return {
      conversations,
      open: sumBy(rows, (r) => r.open),
      resolved: sumBy(rows, (r) => r.resolved),
      closed: sumBy(rows, (r) => r.closed),
      unanswered: sumBy(rows, (r) => r.unanswered),
      slaMeasured,
      slaBreached,
      slaWithin: slaMeasured - slaBreached,
      slaBreachPct: ratePct(slaBreached, slaMeasured),
      olaMeasured,
      olaBreached,
      olaWithin: olaMeasured - olaBreached,
      olaBreachPct: ratePct(olaBreached, olaMeasured),
      csatCount,
      // Each group's average is weighted by the volume behind it — a group of
      // three conversations must not move the org number like one of three
      // hundred.
      csatAvg: weightedMean(rows.map((r) => [r.csatAvg, r.csatCount])),
      avgResponse: weightedMean(rows.map((r) => [r.avgResponseSeconds, r.conversations])),
      avgResolution: weightedMean(rows.map((r) => [r.avgResolutionSeconds, r.resolved])),
    }
  }, [rows])

  const byVolume = useMemo(
    () => [...rows].sort((a, b) => b.conversations - a.conversations),
    [rows],
  )
  const maxVolume = byVolume[0]?.conversations ?? 0
  const top = useMemo(() => byVolume.slice(0, CHART_GROUPS), [byVolume])

  const volumeData = useMemo(
    () => ({
      labels: top.map((r) => r.groupLabel),
      datasets: [
        barDataset('Resolved', top.map((r) => r.resolved), VIZ.series[2], { stacked: true }),
        barDataset('Open', top.map((r) => r.open), VIZ.series[0], { stacked: true }),
      ],
    }),
    [top],
  )

  const timeData = useMemo(
    () => ({
      labels: top.map((r) => r.groupLabel),
      datasets: [
        barDataset('First response', top.map((r) => r.avgResponseSeconds ?? 0), VIZ.series[0]),
        barDataset('Resolution', top.map((r) => r.avgResolutionSeconds ?? 0), VIZ.series[1]),
      ],
    }),
    [top],
  )

  const breachGroups = useMemo(
    () => byVolume.filter((r) => r.slaMeasured > 0).slice(0, CHART_GROUPS),
    [byVolume],
  )
  const breachData = useMemo(
    () => ({
      labels: breachGroups.map((r) => r.groupLabel),
      // One series, one hue: bar length already encodes the rate.
      datasets: [
        barDataset('SLA breach rate', breachGroups.map((r) => r.slaBreachPct ?? 0), VIZ.status.critical),
      ],
    }),
    [breachGroups],
  )

  const attention = useMemo((): AttentionItem[] => {
    return rows
      .filter((r) => r.slaMeasured > 0 && (r.slaBreachPct ?? 0) > BREACH_ALERT)
      .sort((a, b) => (b.slaBreachPct ?? 0) - (a.slaBreachPct ?? 0))
      .slice(0, 8)
      .map((r) => ({
        id: r.groupKey,
        title: r.groupLabel,
        detail: `${formatCount(r.slaBreached)} breached of ${formatCount(r.slaMeasured)} measured`,
        value: formatPct(r.slaBreachPct, 1),
        tone: (r.slaBreachPct ?? 0) > 25 ? ('critical' as const) : ('warning' as const),
      }))
  }, [rows])

  if (rows.length === 0) {
    return (
      <AnalyticsCanvas>
        <div className="rounded-[4px] border border-slate-200 bg-white px-6 py-16 text-center">
          <p className="text-sm font-semibold text-slate-900">Nothing to analyse</p>
          <p className="mt-1 text-xs text-slate-500">
            The current filters returned no conversations. Widen the window or clear a filter, then
            run the report again.
          </p>
        </div>
      </AnalyticsCanvas>
    )
  }

  const leader = byVolume[0]

  return (
    <AnalyticsCanvas>
      <HeroBand
        eyebrow="Conversations in scope"
        value={formatCount(totals.conversations)}
        caption={`Across ${rows.length} ${groupLabel.toLowerCase()}${rows.length === 1 ? '' : 's'} in the selected window`}
        spark={byVolume.map((r) => r.conversations).slice(0, 24)}
        Icon={Building2}
        facts={[
          { label: 'Resolved', value: formatCount(totals.resolved) },
          { label: 'Still open', value: formatCount(totals.open) },
          {
            label: 'SLA breached',
            value: formatPct(totals.slaBreachPct, 1),
            tone: breachTone(totals.slaBreachPct),
          },
          { label: 'Median first response', value: formatSeconds(totals.avgResponse) },
        ]}
      />

      <KpiRow>
        <StatTile
          label="Closed"
          value={formatCount(totals.closed)}
          hint={`${formatPct(ratePct(totals.closed, totals.conversations), 1)} of volume`}
        />
        <StatTile
          label="Awaiting first reply"
          value={formatCount(totals.unanswered)}
          hint={`${formatPct(ratePct(totals.unanswered, totals.conversations), 1)} of volume`}
          tone={totals.unanswered > 0 ? 'warning' : 'good'}
        />
        <StatTile label="Avg first response" value={formatSeconds(totals.avgResponse)} />
        <StatTile label="Avg resolution" value={formatSeconds(totals.avgResolution)} />
        <StatTile
          label="OLA breached"
          value={formatPct(totals.olaBreachPct, 1)}
          tone={breachTone(totals.olaBreachPct)}
          hint={`${formatCount(totals.olaBreached)} of ${formatCount(totals.olaMeasured)} measured`}
        />
        <StatTile
          label="Avg CSAT"
          value={totals.csatAvg === null ? '—' : totals.csatAvg.toFixed(2)}
          hint={totals.csatCount > 0 ? `${formatCount(totals.csatCount)} scored` : 'No survey responses'}
        />
      </KpiRow>

      {/* ── The breakup itself ── */}
      <section className="flex flex-col gap-3">
        <SectionLabel note={`${rows.length} row${rows.length === 1 ? '' : 's'}`}>
          {groupLabel} breakup
        </SectionLabel>
        <ChartCard
          title={`Every ${groupLabel.toLowerCase()}, ranked by volume`}
          subtitle="Bar length shows the share of the busiest row's volume"
          Icon={ListOrdered}
          badge="Scroll for more"
          height={340}
          wide
          footnote={
            leader
              ? `${leader.groupLabel} carries ${formatPct(leader.sharePct, 1)} of the conversations in this window.`
              : undefined
          }
        >
          <RankTable
            rows={byVolume}
            keyOf={(r) => r.groupKey}
            nameOf={(r) => r.groupLabel}
            shareOf={(r) => (maxVolume > 0 ? r.conversations / maxVolume : 0)}
            columns={[
              { label: 'Conversations', align: 'right', render: (r) => formatCount(r.conversations) },
              { label: 'Share', align: 'right', render: (r) => formatPct(r.sharePct, 1) },
              { label: 'Open', align: 'right', render: (r) => formatCount(r.open) },
              { label: 'Resolved', align: 'right', render: (r) => formatCount(r.resolved) },
              { label: 'Unanswered', align: 'right', render: (r) => formatCount(r.unanswered) },
              { label: 'Avg response', align: 'right', render: (r) => formatSeconds(r.avgResponseSeconds) },
              { label: 'Avg resolution', align: 'right', render: (r) => formatSeconds(r.avgResolutionSeconds) },
              {
                label: 'SLA breach',
                align: 'right',
                render: (r) => <BreachCell value={r.slaBreachPct} />,
              },
              {
                label: 'CSAT',
                align: 'right',
                render: (r) => (r.csatAvg === null ? '—' : r.csatAvg.toFixed(2)),
              },
            ]}
          />
        </ChartCard>
      </section>

      {/* ── Throughput ── */}
      <section className="flex flex-col gap-3">
        <SectionLabel>Throughput</SectionLabel>
        <CardGrid>
          <ChartCard
            title="Resolved vs still open"
            subtitle={`Top ${Math.min(CHART_GROUPS, top.length)} by volume`}
            Icon={Gauge}
          >
            {top.length === 0 ? (
              <CardEmpty message="No conversations in this window." />
            ) : (
              <Chart
                type="bar"
                data={volumeData}
                options={barOptions({ horizontal: true, legend: true, stacked: true })}
                className="h-full w-full"
              />
            )}
          </ChartCard>

          <ChartCard
            title="Handling time"
            subtitle="Average first response and resolution"
            Icon={Timer}
          >
            {top.length === 0 ? (
              <CardEmpty message="Nothing was responded to or resolved in this window." />
            ) : (
              <Chart
                type="bar"
                data={timeData}
                options={barOptions({ horizontal: true, legend: true, format: formatSeconds })}
                className="h-full w-full"
              />
            )}
          </ChartCard>
        </CardGrid>
      </section>

      {/* ── Service levels ── */}
      <section className="flex flex-col gap-3">
        <SectionLabel note={`Alert over ${BREACH_ALERT}%`}>Service levels</SectionLabel>
        <CardGrid>
          <ChartCard
            title="SLA breach rate"
            subtitle={`By ${groupLabel.toLowerCase()}, highest volume first`}
            Icon={Clock}
          >
            {breachGroups.length === 0 ? (
              <CardEmpty message="No conversation in this window had an SLA evaluated." />
            ) : (
              <Chart
                type="bar"
                data={breachData}
                options={barOptions({
                  horizontal: true,
                  max: 100,
                  format: (v) => `${Math.round(v)}%`,
                })}
                className="h-full w-full"
              />
            )}
          </ChartCard>

          <ChartCard
            title="Needs attention"
            subtitle={`Rows breaching more than ${BREACH_ALERT}% of measured conversations`}
            Icon={AlertTriangle}
          >
            <AttentionList
              items={attention}
              emptyMessage={`No ${groupLabel.toLowerCase()} is breaching more than ${BREACH_ALERT}% of its conversations.`}
            />
          </ChartCard>

          <ChartCard title="SLA outcome" subtitle="Every conversation with an SLA evaluated" Icon={ShieldCheck} height={220}>
            <Meter
              segments={[
                { label: 'Within SLA', value: totals.slaWithin, color: VIZ.status.good },
                { label: 'Breached', value: totals.slaBreached, color: VIZ.status.critical },
              ]}
              emptyMessage="No conversation in this window had an SLA evaluated."
            />
          </ChartCard>

          <ChartCard title="Where conversations stand" subtitle="Resolved, closed and still open" height={220}>
            <Meter
              segments={[
                { label: 'Resolved', value: totals.resolved, color: VIZ.series[2] },
                { label: 'Open', value: totals.open, color: VIZ.series[0] },
              ]}
              emptyMessage="No conversations in this window."
            />
          </ChartCard>
        </CardGrid>
      </section>
    </AnalyticsCanvas>
  )
}

/** Breach cell: the number leads, a dot carries the state so it isn't color-only. */
function BreachCell({ value }: { value: number | null }): JSX.Element {
  const tone = breachTone(value)
  const dot =
    tone === 'good' ? 'bg-[#0CA30C]' : tone === 'warning' ? 'bg-[#FAB219]' : tone === 'critical' ? 'bg-[#D03B3B]' : 'bg-slate-300'
  return (
    <span className="inline-flex items-center justify-end gap-1.5">
      <span className={`h-1.5 w-1.5 rounded-full ${dot}`} aria-hidden="true" />
      {formatPct(value, 1)}
    </span>
  )
}

/** Breach-rate tone: 0–5% good, 5–15% warning, worse than that critical. */
function breachTone(pct: number | null): 'default' | 'good' | 'warning' | 'critical' {
  if (pct === null) return 'default'
  if (pct <= 5) return 'good'
  if (pct <= 15) return 'warning'
  return 'critical'
}
